import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { fmtMoney, fmtDate, studentLabel } from "@/lib/helpers";
import { Plus, Trash, ArrowLeft, Receipt, CreditCard, ArrowCounterClockwise, QrCode, CheckCircle, WarningCircle, FilePdf, Briefcase } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAddons } from "@/contexts/AddonsContext";

import { devLog } from "@/lib/devLog";
const STATUS_STYLES = {
  draft: "bg-slate-200 text-slate-800 border-slate-300",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  partial: "bg-amber-100 text-amber-800 border-amber-200",
  unpaid: "bg-red-100 text-red-800 border-red-200",
};

export function BillingListPage() {
  const { activeBranch, activeRole } = useAuth();
  const { can } = usePermissions();
  const { hasEnterpriseWorkforce } = useAddons();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const navigate = useNavigate();

  const branchId = activeBranch === "all" ? null : activeBranch;
  const canCreate = can("invoice", "limited") || ["owner","admin","reception","accountant"].includes(activeRole);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/invoices", { params: branchId ? { branch_id: branchId } : {} });
      setInvoices(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activeBranch]); // eslint-disable-line

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="overline mb-1.5">Finance</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Billing</h1>
          <p className="text-sm text-muted-foreground mt-1">{invoices.length} invoice{invoices.length!==1 && "s"}</p>
        </div>
        {canCreate && <Button onClick={() => setShowNew(true)} className="gap-1.5" data-testid="new-invoice-btn"><Plus size={16} weight="bold" />New invoice</Button>}
      </div>

      {hasEnterpriseWorkforce ? (
        <Tabs defaultValue="student" data-testid="billing-tabs">
          <TabsList>
            <TabsTrigger value="student" data-testid="billing-tab-student">
              <Receipt size={13} className="mr-1.5" /> Student billing
            </TabsTrigger>
            <TabsTrigger value="workforce" data-testid="billing-tab-workforce">
              <Briefcase size={13} className="mr-1.5" /> Workforce &amp; payroll
            </TabsTrigger>
          </TabsList>
          <TabsContent value="student" className="mt-4 space-y-6">
            <StudentBillingSection loading={loading} invoices={invoices} navigate={navigate} />
          </TabsContent>
          <TabsContent value="workforce" className="mt-4">
            <WorkforceBillingPlaceholder />
          </TabsContent>
        </Tabs>
      ) : (
        <StudentBillingSection loading={loading} invoices={invoices} navigate={navigate} />
      )}

      <NewInvoiceDialog open={showNew} onOpenChange={setShowNew} onCreated={() => { setShowNew(false); load(); }} />
    </div>
  );
}


// -----------------------------------------------------------------
// Extracted content sections so the top-level render tree stays flat.
// -----------------------------------------------------------------

function StudentBillingSection({ loading, invoices, navigate }) {
  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (invoices.length === 0) return (
    <Card><CardContent className="p-10 text-center">
      <Receipt size={32} className="text-muted-foreground/40 mx-auto mb-2" weight="duotone" />
      <div className="text-sm text-muted-foreground">No invoices yet.</div>
    </CardContent></Card>
  );
  return (
    <Card><CardContent className="p-0">
      <div className="divide-y divide-border">
        {invoices.map(inv => (
          <button key={inv.id} onClick={() => navigate(`/billing/${inv.id}`)} className="w-full px-5 py-3.5 flex items-center gap-4 hover:bg-muted/40 text-left" data-testid={`invoice-row-${inv.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">{inv.invoice_no}</div>
              <div className="text-xs text-muted-foreground">
                {inv.student_ref && (
                  <span className="font-mono font-semibold text-primary mr-1">{inv.student_ref} ·</span>
                )}
                {inv.student_name} · {fmtDate(inv.created_at)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold tabular-nums">{fmtMoney(inv.total)}</div>
              {inv.balance > 0.01 && <div className="text-xs text-amber-700">{fmtMoney(inv.balance)} due</div>}
            </div>
            <Badge className={STATUS_STYLES[inv.status]}>{inv.status}</Badge>
          </button>
        ))}
      </div>
    </CardContent></Card>
  );
}


function WorkforceBillingPlaceholder() {
  // Phase 4-5 will populate this with salary disbursements, consultant
  // payments, reimbursements, advances, loans. For Phase 1 we just
  // architecturally reserve the tab so admins understand the workspace's
  // capabilities without seeing an empty screen.
  return (
    <Card data-testid="workforce-billing-placeholder">
      <CardContent className="p-10 text-center space-y-2">
        <Briefcase size={32} className="text-primary/40 mx-auto" weight="duotone" />
        <div className="text-sm font-medium">Workforce &amp; payroll transactions</div>
        <div className="text-xs text-muted-foreground max-w-md mx-auto">
          Salary disbursements, consultant payments, reimbursements, advances, and
          loans will appear here once the Payroll and Compensation Engine (Phase 4)
          starts generating transactions. Employee master lives at
          <span className="font-mono"> /workforce/employees</span>.
        </div>
      </CardContent>
    </Card>
  );
}

function NewInvoiceDialog({ open, onOpenChange, onCreated }) {
  const { activeBranch } = useAuth();
  const [students, setStudents] = useState([]);
  const [therapyTypes, setTherapyTypes] = useState([]);
  const [studentId, setStudentId] = useState("");
  const nextLineUid = useRef(0);
  const makeLine = (patch = {}) => ({ _uid: `l-${++nextLineUid.current}`, description: "", quantity: 1, unit_price: 0, line_type: "billed", ...patch });
  const [lines, setLines] = useState([makeLine()]);
  const [discount, setDiscount] = useState(0);
  const [tax, setTax] = useState(0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  // iter-67f — Therapy Plan Template picker + editable invoice
  // period. Both are optional; leaving them blank preserves the
  // existing manual invoice behaviour.
  const [templates, setTemplates] = useState([]);
  const [selectedTplId, setSelectedTplId] = useState("");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  // iter-67i (ID-39) — Parity with the Sessions/InvoiceDetail flow:
  // let the biller apply a Grant Donation at invoice-create time.
  const [grants, setGrants] = useState([]);
  const [chosenGrant, setChosenGrant] = useState("");
  const [grantAmount, setGrantAmount] = useState("");
  const branchId = activeBranch === "all" ? null : activeBranch;

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        // iter-67i (ID-31) — Isolate the student picker to the currently
        // active branch. Fall back to "all" only when the user has "All
        // Branches" selected, so multi-branch admins can still cross-list.
        const studentParams = branchId ? { branch_id: branchId } : { branch_id: "all" };
        const [s, tt, tpls] = await Promise.all([
          api.get("/students", { params: studentParams }),
          api.get("/therapy-types"),
          api.get("/therapy-plan-templates?status=active").catch(() => ({ data: [] })),
        ]);
        setStudents(s.data || []);
        setTherapyTypes(tt.data || []);
        setTemplates(tpls.data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [open, branchId]);

  // iter-67i (ID-39) — Grant list for the picked student. Empty when
  // no student is chosen or no grants cover them; the panel stays
  // hidden and behaves identically to InvoiceDetailPage.
  useEffect(() => {
    if (!open || !studentId) { setGrants([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/billing/grants-for-student/${studentId}`);
        setGrants(data || []);
      } catch (err) { devLog("grants-for-student fetch failed", err); }
    })();
  }, [open, studentId]);

  const subtotalBilled = lines.reduce((s, l) => (l.line_type === "donation" ? s : s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0)), 0);
  const donationCredit = lines.reduce((s, l) => (l.line_type === "donation" ? s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0) : s), 0);
  const subtotal = subtotalBilled - donationCredit;
  const afterDisc = Math.max(0, subtotal - Number(discount || 0));
  const taxAmt = Math.round(afterDisc * (Number(tax || 0) / 100) * 100) / 100;
  const total = Math.round((afterDisc + taxAmt) * 100) / 100;

  const setLine = (i, key, val) => {
    const ls = [...lines]; ls[i] = { ...ls[i], [key]: val }; setLines(ls);
  };
  const addLine = () => setLines([...lines, makeLine()]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const fillFromTherapy = (i, tt_id) => {
    const tt = therapyTypes.find(t => t.id === tt_id);
    if (!tt) return;
    // iter-67j (Area 3) — Only autofill blank rows. If either
    // description or unit_price has been touched, leave the line
    // alone — the user's edits are the source of truth.
    const line = lines[i];
    const hasDesc = (line?.description || "").trim().length > 0;
    const hasPrice = Number(line?.unit_price || 0) > 0;
    if (hasDesc || hasPrice) return;
    setLine(i, "description", `${tt.name} session (${tt.duration_min} min)`);
    setLine(i, "unit_price", tt.rate);
    setLine(i, "therapy_type_id", tt_id);
  };

  // iter-67i (ID-39) — Add a donation credit line sourced from a grant.
  // Mirrors InvoiceDetailPage.addDonationLine exactly so the two flows
  // stay identical.
  const addDonationLine = () => {
    if (!chosenGrant) return;
    const g = grants.find(x => x.id === chosenGrant);
    if (!g) return;
    const raw = Number(grantAmount || 0);
    if (raw <= 0) { toast.error("Enter a donation amount"); return; }
    if (raw > g.balance) {
      toast.error(`Grant "${g.name}" only has ${fmtMoney(g.balance)} remaining`);
      return;
    }
    setLines(prev => [...prev, makeLine({
      description: `Donation — ${g.name}${g.donor ? ` (${g.donor})` : ""}`,
      quantity: 1,
      unit_price: raw,
      line_type: "donation",
      grant_id: g.id,
    })]);
    setChosenGrant("");
    setGrantAmount("");
    toast.success("Donation line added");
  };

  const submit = async () => {
    if (!studentId) { toast.error("Pick a student"); return; }
    const picked = students.find(s => s.id === studentId);
    if (picked && !picked.branch_id) {
      toast.error(`${picked.name} has no branch assigned — open their profile and set a branch before raising an invoice.`);
      return;
    }
    if (!branchId) { toast.error("Pick a branch first"); return; }
    setSaving(true);
    try {
      await api.post("/billing/invoices", {
        student_id: studentId,
        branch_id: branchId,
        lines: lines.map(l => ({
          description: l.description,
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price),
          therapy_type_id: l.therapy_type_id || null,
          // iter-67i (ID-39) — Persist donation lines + their grant
          // link so the backend auto-allocates on confirm.
          line_type: l.line_type || "billed",
          grant_id: l.grant_id || null,
        })),
        discount: Number(discount),
        tax_pct: Number(tax),
        notes,
        // iter-67f — Persist the chosen template so that on-paid the
        // backend can auto-create a Therapy Plan for the student.
        template_id: selectedTplId || null,
        period_from: periodFrom || null,
        period_to: periodTo || null,
      });
      toast.success("Invoice created");
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // iter-67f — Populate line-items + fee from the picked template.
  // Editable afterwards; the template is a starting point, not a lock.
  const applyTemplate = (tplId) => {
    setSelectedTplId(tplId);
    if (!tplId) return;
    const tpl = templates.find(t => t.id === tplId);
    if (!tpl) return;
    const perSession = tpl.fee_mode === "per_session"
      ? Number(tpl.fee_per_session || 0) : null;
    const flatFee = tpl.fee_mode === "flat_period"
      ? Number(tpl.flat_period_fee || 0) : null;
    const ttById = Object.fromEntries((therapyTypes || []).map(t => [t.id, t]));
    const newLines = (tpl.line_items || []).map(li => {
      const tt = ttById[li.therapy_type_id];
      const ttName = tt?.name || "Therapy session";
      return {
        _uid: `tpl-${li.therapy_type_id}-${Math.random()}`,
        description: `${ttName} — ${li.sessions_per_week}/wk × ${li.total_sessions} sessions`,
        quantity: perSession != null ? Number(li.total_sessions || 0) : 1,
        unit_price: perSession != null
          ? perSession
          : (flatFee || 0) / Math.max(1, (tpl.line_items || []).length),
        therapy_type_id: li.therapy_type_id,
      };
    });
    if (newLines.length) setLines(newLines);
    // No changes to discount / tax — the admin controls those.
    toast.success(`Applied "${tpl.name}". All fields remain editable.`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display tracking-tight">New invoice</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Student *</Label>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger data-testid="invoice-student"><SelectValue placeholder="Select student" /></SelectTrigger>
              <SelectContent className="bg-card">
                {students.map(s => {
                  const st = s.enrolment_status || "registered";
                  const suffix = st !== "registered" ? ` · ${st === "pending" ? "Pending" : "Not registered"}` : "";
                  const noBranch = !s.branch_id ? " · No branch" : "";
                  return (
                    <SelectItem key={s.id} value={s.id} data-testid={`invoice-student-opt-${s.id}`}>
                      {studentLabel(s)}{suffix}{noBranch}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {(() => {
              const picked = students.find(s => s.id === studentId);
              if (picked && !picked.branch_id) {
                return (
                  <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid="invoice-no-branch-warning">
                    {picked.name} has no branch assigned yet. Open their profile and set a branch before raising an invoice.
                  </div>
                );
              }
              return null;
            })()}
          </div>

          <div className="space-y-2">
            <Label>Line items</Label>
            {/* iter-67i (ID-39) — Grant → Donation picker for parity with
                Sessions / InvoiceDetail. Hidden until a covered student
                is chosen. Adds a donation credit line the backend
                allocates against the grant on confirm. */}
            {grants.length > 0 && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid="grant-picker-card-new">
                <div>
                  <div className="text-sm font-medium text-emerald-900">Apply donation from a grant</div>
                  <div className="text-[11px] text-emerald-800/80">This student is covered by {grants.length} grant{grants.length !== 1 ? "s" : ""}. Add a donation credit that reduces the payable amount.</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Select value={chosenGrant} onValueChange={setChosenGrant}>
                    <SelectTrigger className="sm:col-span-2" data-testid="grant-select-new"><SelectValue placeholder="Choose a grant" /></SelectTrigger>
                    <SelectContent className="bg-card">
                      {grants.map(g => (
                        <SelectItem key={g.id} value={g.id} disabled={g.balance <= 0}>
                          {g.name}{g.donor ? ` · ${g.donor}` : ""} · balance {fmtMoney(g.balance)}{g.balance <= 0 ? " (exhausted)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Input
                      type="number" min="0" step="0.01"
                      placeholder="Amount"
                      value={grantAmount}
                      onChange={e => setGrantAmount(e.target.value)}
                      data-testid="grant-amount-new"
                    />
                    <Button onClick={addDonationLine} disabled={!chosenGrant || !grantAmount} data-testid="add-donation-line-new">Add</Button>
                  </div>
                </div>
              </div>
            )}
            {/* iter-67f — Optional Therapy Plan Template picker.
                Always visible so admins know the feature exists.
                Selecting one auto-fills line items and fees; every
                field remains editable. */}
            <div className="rounded-md border bg-muted/30 p-2" data-testid="tpl-picker-block">
              <Label className="text-[11px] uppercase tracking-wide">
                Select Therapy Plan Template (optional)
              </Label>
              {templates.length === 0 ? (
                <div
                  className="mt-1 text-[11px] text-muted-foreground italic"
                  data-testid="tpl-picker-empty"
                >
                  No active templates yet. Create one in{" "}
                  <a href="/settings?tab=therapy-plan-templates" className="underline">
                    Settings → Therapy Plan Templates
                  </a>{" "}
                  to auto-fill invoices and auto-create a Therapy Plan
                  when the invoice is paid.
                </div>
              ) : (
                <>
                  <Select value={selectedTplId} onValueChange={applyTemplate}>
                    <SelectTrigger
                      className="mt-1 h-9 text-sm"
                      data-testid="invoice-tpl-picker"
                    >
                      <SelectValue placeholder="No template — manual line items" />
                    </SelectTrigger>
                    <SelectContent className="bg-card">
                      {templates.map(t => (
                        <SelectItem key={t.id} value={t.id} data-testid={`invoice-tpl-opt-${t.id}`}>
                          {t.name} · {t.billing_period_type}
                          {t.billing_period_type === "weekly" ? ` × ${t.weeks_in_period || 1}` : ""}
                          {t.fee_mode === "per_session"
                            ? ` · ₹${t.fee_per_session || 0}/session`
                            : ` · ₹${t.flat_period_fee || 0} flat`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedTplId && (
                    <div className="text-[10px] text-muted-foreground mt-1">
                      Template applied. Line items and fees below remain fully editable.
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={l._uid || `line-${i}`} className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-5">
                    <Input placeholder="Description" value={l.description} onChange={e=>setLine(i, "description", e.target.value)} />
                    <Select onValueChange={v => fillFromTherapy(i, v)}>
                      <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Fill from therapy…" /></SelectTrigger>
                      <SelectContent className="bg-card">
                        {therapyTypes.map(tt => <SelectItem key={tt.id} value={tt.id}>{tt.name} · {fmtMoney(tt.rate)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input className="col-span-2" type="number" min="1" value={l.quantity} onChange={e=>setLine(i, "quantity", e.target.value)} />
                  <Input className="col-span-4" type="number" min="0" step="0.01" value={l.unit_price} onChange={e=>setLine(i, "unit_price", e.target.value)} placeholder="₹" />
                  <Button variant="ghost" size="icon" className="col-span-1" onClick={()=>removeLine(i)}><Trash size={14} /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addLine} className="gap-1.5"><Plus size={14} /> Add line</Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Discount (₹)</Label><Input type="number" value={discount} onChange={e=>setDiscount(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Tax %</Label><Input type="number" value={tax} onChange={e=>setTax(e.target.value)} /></div>
          </div>

          {/* iter-67f — Editable invoice period. Populates
              `invoices.period_from` / `period_to` used by the
              Therapy Plan snapshot when the invoice is marked paid. */}
          <div className="grid grid-cols-2 gap-3" data-testid="invoice-period-block">
            <div className="space-y-1.5">
              <Label>Period from</Label>
              <Input
                type="date"
                value={periodFrom}
                onChange={e => setPeriodFrom(e.target.value)}
                data-testid="invoice-period-from"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Period to</Label>
              <Input
                type="date"
                value={periodTo}
                onChange={e => setPeriodTo(e.target.value)}
                data-testid="invoice-period-to"
              />
            </div>
          </div>

          <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={notes} onChange={e=>setNotes(e.target.value)} /></div>

          <div className="p-4 border border-border rounded-md bg-muted/40 text-sm space-y-1 tabular-nums">
            <div className="flex justify-between"><span>Billed subtotal</span><span>{fmtMoney(subtotalBilled)}</span></div>
            {donationCredit > 0 && (
              <div className="flex justify-between text-emerald-700"><span>Donation credit</span><span>−{fmtMoney(donationCredit)}</span></div>
            )}
            <div className="flex justify-between"><span>Discount</span><span>−{fmtMoney(discount)}</span></div>
            <div className="flex justify-between"><span>Tax</span><span>{fmtMoney(taxAmt)}</span></div>
            <div className="flex justify-between font-semibold text-base pt-2 border-t border-border"><span>Total</span><span>{fmtMoney(total)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-invoice">{saving ? "Creating…" : "Create invoice"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeRole } = useAuth();
  const [inv, setInv] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [refundTarget, setRefundTarget] = useState(null); // payment being refunded
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("requested_by_customer");
  const [refunding, setRefunding] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/billing/invoices/${id}`);
      setInv(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const submitPayment = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Enter amount"); return; }
    try {
      await api.post(`/billing/invoices/${id}/payments`, { amount: Number(amount), method, reference });
      toast.success("Payment recorded");
      setShowPay(false); setAmount(""); setReference("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [payingOnline, setPayingOnline] = useState(false);
  const [upiIntent, setUpiIntent] = useState(null);   // { upi_uri, upi_id, payee_name, amount, invoice_no, invoice_id }
  const [upiTxnRef, setUpiTxnRef] = useState("");
  const [upiSubmitting, setUpiSubmitting] = useState(false);
  const [billingSettings, setBillingSettings] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/settings/billing");
        setBillingSettings(data);
      } catch (err) { devLog("billing settings not configured", err); }
    })();
  }, []);

  const payOnline = async () => {
    setPayingOnline(true);
    try {
      const { data } = await api.post("/payments/upi/intent", { invoice_id: id });
      setUpiIntent(data);
      setUpiTxnRef("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPayingOnline(false);
    }
  };

  const payWithStripe = async () => {
    setPayingOnline(true);
    try {
      const { data } = await api.post("/payments/invoice-checkout", {
        invoice_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPayingOnline(false);
    }
  };

  // Razorpay Checkout — loads the hosted script on demand, opens the modal,
  // and on success POSTs the returned {order_id, payment_id, signature} to
  // our backend where the HMAC signature is verified and the payment is
  // recorded against the invoice.
  const loadRazorpayScript = () => new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("Failed to load Razorpay Checkout"));
    document.body.appendChild(s);
  });

  const payWithRazorpay = async () => {
    setPayingOnline(true);
    try {
      await loadRazorpayScript();
      const { data: order } = await api.post("/payments/razorpay/order", { invoice_id: id });
      const rp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,           // paise
        currency: order.currency,
        order_id: order.order_id,
        name: billingSettings?.company_name || "TherapyFlow",
        description: `Invoice ${order.invoice_no}`,
        prefill: {
          name: inv?.student?.name || "",
          email: billingSettings?.contact_email || "",
        },
        theme: { color: "#4A6753" },
        handler: async (res) => {
          try {
            await api.post("/payments/razorpay/verify", {
              invoice_id: id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment received — invoice updated");
            load();
          } catch (e) { toast.error(formatApiError(e)); }
          finally { setPayingOnline(false); }
        },
        modal: {
          ondismiss: () => setPayingOnline(false),
        },
      });
      rp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp?.error?.description || "unknown error"}`);
        setPayingOnline(false);
      });
      rp.open();
    } catch (e) {
      toast.error(formatApiError(e));
      setPayingOnline(false);
    }
  };

  const downloadPdf = (type) => {
    const url = `${API_BASE}/billing/invoices/${id}/pdf?type=${type}`;
    // Same-origin fetch; browser attaches the httpOnly access_token
    // cookie automatically when `credentials: "include"` is set.
    fetch(url, { credentials: "include" })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then(blob => {
        const a = document.createElement("a");
        const objUrl = URL.createObjectURL(blob);
        a.href = objUrl;
        a.download = `${type === "receipt" ? "Receipt" : "Invoice"}_${inv?.invoice_no || id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
      })
      .catch(e => toast.error(`Could not download ${type} PDF: ${e.message}`));
  };

  const submitUpiConfirm = async () => {
    if (!upiIntent) return;
    if (!upiTxnRef.trim() || upiTxnRef.trim().length < 3) {
      toast.error("Enter the UPI transaction reference from your app");
      return;
    }
    setUpiSubmitting(true);
    try {
      await api.post("/payments/upi/confirm", {
        invoice_id: id,
        txn_ref: upiTxnRef.trim(),
        amount: upiIntent.amount,
      });
      toast.success(`Recorded ₹${upiIntent.amount.toFixed(2)} — awaiting admin verification`);
      setUpiIntent(null);
      setUpiTxnRef("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUpiSubmitting(false);
    }
  };

  const [verifyingId, setVerifyingId] = useState(null);
  const verifyUpi = async (payment, verify = true) => {
    setVerifyingId(payment.id);
    try {
      await api.post("/payments/upi/verify", {
        invoice_id: id,
        payment_id: payment.id,
        verified: verify,
      });
      toast.success(verify ? "Payment marked as verified" : "Verification removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setVerifyingId(null); }
  };

  // Build a map: paymentId -> already-refunded absolute amount
  const refundedByPayment = {};
  for (const p of (inv?.payments || [])) {
    if (p.method === "stripe_refund" && p.refund_of) {
      refundedByPayment[p.refund_of] = (refundedByPayment[p.refund_of] || 0) + Math.abs(p.amount);
    }
  }

  const openRefund = (payment) => {
    const alreadyRefunded = refundedByPayment[payment.id] || 0;
    const remaining = Math.max(0, Math.round((payment.amount - alreadyRefunded) * 100) / 100);
    setRefundTarget(payment);
    setRefundAmount(String(remaining));
    setRefundReason("requested_by_customer");
  };

  const submitRefund = async () => {
    if (!refundTarget) return;
    const amt = Number(refundAmount);
    if (!amt || amt <= 0) { toast.error("Enter a refund amount"); return; }
    setRefunding(true);
    try {
      await api.post(`/payments/invoices/${id}/refund`, {
        payment_id: refundTarget.id,
        amount: amt,
        reason: refundReason || undefined,
      });
      toast.success(`Refunded ${fmtMoney(amt)}`);
      setRefundTarget(null);
      setRefundAmount("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRefunding(false); }
  };

  if (!inv) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const canPay = ["owner","admin","reception","accountant","guardian"].includes(activeRole);
  const canRefund = ["owner","admin","accountant"].includes(activeRole);
  const canEditDraft = ["owner","admin","reception","accountant"].includes(activeRole);

  // Draft mode — render the editable form INSTEAD of the read-only invoice
  // until the user confirms it. The card below is reused for the confirmed
  // state so we keep the existing invoice UI intact.
  if (inv.status === "draft") {
    return (
      <div className="space-y-6 max-w-3xl">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1.5 -ml-2"><ArrowLeft size={14} /> Back</Button>
        <DraftInvoiceEditor
          inv={inv}
          canEdit={canEditDraft}
          onSaved={load}
          onConfirmed={() => load()}
          onDeleted={() => navigate("/billing")}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1.5 -ml-2"><ArrowLeft size={14} /> Back</Button>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="overline mb-1.5">Invoice</div>
              <div className="font-display text-3xl font-bold tracking-tight">{inv.invoice_no}</div>
              <div className="text-sm text-muted-foreground mt-1">
                {inv.student?.student_id && (
                  <span className="font-mono font-semibold text-primary mr-1">{inv.student.student_id} ·</span>
                )}
                {inv.student?.name} · {fmtDate(inv.created_at)}
              </div>
            </div>
            <Badge className={STATUS_STYLES[inv.status]}>{inv.status}</Badge>
          </div>

          <div className="mt-6 divide-y divide-border overflow-x-auto">
            <div className="min-w-[520px]">
            <div className="grid grid-cols-12 py-2 text-xs overline">
              <div className="col-span-6">Description</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-2 text-right">Unit</div>
              <div className="col-span-2 text-right">Amount</div>
            </div>
            {inv.lines.map((l, i) => (
              <div key={`${l.description || "l"}-${l.unit_price}-${i}`} className="grid grid-cols-12 py-2.5 text-sm">
                <div className="col-span-6">{l.description}</div>
                <div className="col-span-2 text-right tabular-nums">{l.quantity}</div>
                <div className="col-span-2 text-right tabular-nums">{fmtMoney(l.unit_price)}</div>
                <div className="col-span-2 text-right tabular-nums">{fmtMoney(l.quantity * l.unit_price)}</div>
              </div>
            ))}
            </div>
          </div>

          <div className="mt-4 space-y-1 text-sm tabular-nums max-w-xs ml-auto">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmtMoney(inv.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span>−{fmtMoney(inv.discount)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Tax ({inv.tax_pct}%)</span><span>{fmtMoney(inv.tax)}</span></div>
            <div className="flex justify-between font-display font-bold text-lg pt-2 border-t border-border"><span>Total</span><span>{fmtMoney(inv.total)}</span></div>
            <div className="flex justify-between text-emerald-700"><span>Paid</span><span>{fmtMoney(inv.paid)}</span></div>
            <div className="flex justify-between font-semibold text-amber-700"><span>Balance</span><span>{fmtMoney(inv.balance)}</span></div>
          </div>

          {canPay && inv.balance > 0.01 && (
            <div className="mt-6 flex flex-col md:flex-row justify-end gap-2">
              {activeRole !== "guardian" && (
                <Button
                  variant="outline"
                  onClick={() => { setAmount(String(inv.balance)); setShowPay(true); }}
                  data-testid="record-payment"
                  className="gap-1.5"
                >
                  Record manual payment
                </Button>
              )}
              <Button
                onClick={payOnline}
                disabled={payingOnline}
                data-testid="pay-online"
                className="gap-1.5"
                style={{ display: (!billingSettings || billingSettings.upi_enabled) ? undefined : "none" }}
              >
                <QrCode size={16} weight="duotone" />
                {payingOnline ? "Opening…" : `Pay ${fmtMoney(inv.balance)} via UPI`}
              </Button>
              {billingSettings?.stripe_enabled && (
                <Button
                  onClick={payWithStripe}
                  disabled={payingOnline}
                  variant="outline"
                  data-testid="pay-stripe"
                  className="gap-1.5"
                >
                  <CreditCard size={16} weight="duotone" />
                  Pay by card (Stripe)
                </Button>
              )}
              {billingSettings?.razorpay_enabled && (
                <Button
                  onClick={payWithRazorpay}
                  disabled={payingOnline}
                  variant="outline"
                  data-testid="pay-razorpay"
                  className="gap-1.5"
                >
                  <CreditCard size={16} weight="duotone" />
                  Pay via Razorpay
                </Button>
              )}
            </div>
          )}
          {inv.status === "paid" && (
            <div className="mt-6 text-right text-sm text-emerald-700 font-medium">Fully paid · thank you</div>
          )}

          {/* PDF download row — always visible so anyone can grab a copy */}
          <div className="mt-4 pt-4 border-t border-border flex flex-wrap justify-end gap-2">
            <Button
              onClick={() => downloadPdf("invoice")}
              variant="outline"
              size="sm"
              data-testid="download-invoice-pdf"
              className="gap-1.5"
            >
              <FilePdf size={14} weight="duotone" />
              Download invoice PDF
            </Button>
            {inv.status === "paid" && (
              <Button
                onClick={() => downloadPdf("receipt")}
                variant="outline"
                size="sm"
                data-testid="download-receipt-pdf"
                className="gap-1.5 border-emerald-200 text-emerald-800 hover:bg-emerald-50"
              >
                <FilePdf size={14} weight="duotone" />
                Download paid receipt
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {inv.payments?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base font-display tracking-tight">Payments</CardTitle></CardHeader>
          <CardContent className="divide-y divide-border p-0">
            {inv.payments.map((p, i) => {
              const isRefund = p.method === "stripe_refund";
              const alreadyRefunded = refundedByPayment[p.id] || 0;
              const remaining = Math.max(0, Math.round((p.amount - alreadyRefunded) * 100) / 100);
              const canRefundThis = canRefund && p.method === "stripe" && remaining > 0.01;
              // Verification state applies only to UPI guardian-submitted payments (they have verified:false initially).
              const isUpi = p.method === "upi";
              const isUnverifiedUpi = isUpi && p.verified === false;
              const isVerifiedUpi = isUpi && p.verified === true;
              return (
                <div key={p.id || i} className="flex items-center px-5 py-3 gap-3" data-testid={`payment-row-${p.id || i}`}>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${isRefund ? "text-rose-700" : ""}`}>
                      {isRefund ? `- ${fmtMoney(Math.abs(p.amount))}` : fmtMoney(p.amount)}
                      {isRefund && <Badge className="ml-2 bg-rose-100 text-rose-800 border-rose-200 text-[10px]">Refund</Badge>}
                      {isUnverifiedUpi && (
                        <Badge className="ml-2 bg-amber-100 text-amber-800 border-amber-200 text-[10px] gap-1" data-testid={`unverified-badge-${p.id}`}>
                          <WarningCircle size={11} weight="duotone" />Unverified
                        </Badge>
                      )}
                      {isVerifiedUpi && (
                        <Badge className="ml-2 bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] gap-1" data-testid={`verified-badge-${p.id}`}>
                          <CheckCircle size={11} weight="duotone" />Verified
                        </Badge>
                      )}
                      {!isRefund && alreadyRefunded > 0 && (
                        <span className="ml-2 text-xs text-muted-foreground">· {fmtMoney(alreadyRefunded)} refunded</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {fmtDate(p.received_at)} · {p.method}
                      {p.reference && ` · ${p.reference}`}
                      {isRefund && p.reason && ` · ${p.reason.replace(/_/g, " ")}`}
                    </div>
                  </div>
                  {canRefund && isUnverifiedUpi && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => verifyUpi(p, true)}
                      disabled={verifyingId === p.id}
                      data-testid={`verify-btn-${p.id}`}
                      className="gap-1.5 border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                    >
                      <CheckCircle size={14} weight="duotone" />
                      {verifyingId === p.id ? "Verifying…" : "Verify"}
                    </Button>
                  )}
                  {canRefund && isVerifiedUpi && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => verifyUpi(p, false)}
                      disabled={verifyingId === p.id}
                      data-testid={`unverify-btn-${p.id}`}
                      className="text-xs text-muted-foreground"
                      title="Undo verification"
                    >
                      {verifyingId === p.id ? "…" : "Undo"}
                    </Button>
                  )}
                  {canRefundThis && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openRefund(p)}
                      data-testid={`refund-btn-${p.id}`}
                      className="gap-1.5"
                    >
                      <ArrowCounterClockwise size={14} weight="duotone" />
                      Refund
                    </Button>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Dialog open={showPay} onOpenChange={setShowPay}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Record payment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Amount</Label><Input type="number" value={amount} onChange={e=>setAmount(e.target.value)} data-testid="payment-amount" /></div>
            <div className="space-y-1.5"><Label>Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card">
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank">Bank transfer</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Reference (optional)</Label><Input value={reference} onChange={e=>setReference(e.target.value)} placeholder="UPI ref, txn id…" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPay(false)}>Cancel</Button>
            <Button onClick={submitPayment} data-testid="confirm-payment">Save payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!refundTarget} onOpenChange={(o) => { if (!o) setRefundTarget(null); }}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Refund Stripe payment</DialogTitle>
          </DialogHeader>
          {refundTarget && (() => {
            const alreadyRefunded = refundedByPayment[refundTarget.id] || 0;
            const remaining = Math.max(0, Math.round((refundTarget.amount - alreadyRefunded) * 100) / 100);
            return (
              <div className="space-y-4">
                <div className="p-3 rounded-md bg-muted/40 border border-border text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Original payment</span><span className="tabular-nums">{fmtMoney(refundTarget.amount)}</span></div>
                  {alreadyRefunded > 0 && (
                    <div className="flex justify-between text-rose-700"><span>Already refunded</span><span className="tabular-nums">−{fmtMoney(alreadyRefunded)}</span></div>
                  )}
                  <div className="flex justify-between font-semibold pt-1 border-t border-border"><span>Refundable</span><span className="tabular-nums">{fmtMoney(remaining)}</span></div>
                </div>
                <div className="space-y-1.5">
                  <Label>Refund amount</Label>
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    max={remaining}
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    data-testid="refund-amount"
                  />
                  <div className="text-[11px] text-muted-foreground">Max {fmtMoney(remaining)} — enter a smaller amount for a partial refund.</div>
                </div>
                <div className="space-y-1.5">
                  <Label>Reason</Label>
                  <Select value={refundReason} onValueChange={setRefundReason}>
                    <SelectTrigger data-testid="refund-reason"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card">
                      <SelectItem value="requested_by_customer">Requested by customer</SelectItem>
                      <SelectItem value="duplicate">Duplicate charge</SelectItem>
                      <SelectItem value="fraudulent">Fraudulent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundTarget(null)} disabled={refunding}>Cancel</Button>
            <Button onClick={submitRefund} disabled={refunding} data-testid="confirm-refund" className="gap-1.5">
              {refunding ? "Refunding…" : "Issue refund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UPI QR modal — India-native online payment */}
      <Dialog open={!!upiIntent} onOpenChange={(o) => { if (!o) { setUpiIntent(null); setUpiTxnRef(""); } }}>
        <DialogContent className="bg-card max-w-md" data-testid="upi-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Pay via UPI</DialogTitle>
          </DialogHeader>
          {upiIntent && (
            <div className="space-y-5">
              <div className="flex flex-col items-center gap-3">
                <div className="p-3 bg-white rounded-md border border-border">
                  <QRCodeSVG value={upiIntent.upi_uri} size={220} level="M" includeMargin={false} data-testid="upi-qr" />
                </div>
                <div className="text-center space-y-0.5">
                  <div className="font-display text-2xl font-bold tracking-tight tabular-nums">₹{upiIntent.amount.toFixed(2)}</div>
                  <div className="text-xs text-muted-foreground">to <span className="font-medium text-foreground">{upiIntent.payee_name}</span> · <span className="font-mono">{upiIntent.upi_id}</span></div>
                  <div className="text-[11px] text-muted-foreground">Invoice {upiIntent.invoice_no}</div>
                </div>
              </div>

              <div className="text-xs text-muted-foreground text-center leading-relaxed">
                Scan this QR with any UPI app (GPay, PhonePe, Paytm, BHIM…) — or{" "}
                <a
                  href={upiIntent.upi_uri}
                  className="text-primary underline underline-offset-2"
                  data-testid="upi-open-app"
                >
                  tap to open your UPI app
                </a>{" "}
                on mobile.
              </div>

              <div className="space-y-1.5 pt-2 border-t border-border">
                <Label htmlFor="upi-txn-ref">UPI transaction reference</Label>
                <Input
                  id="upi-txn-ref"
                  value={upiTxnRef}
                  onChange={(e) => setUpiTxnRef(e.target.value)}
                  placeholder="e.g. 431234567890"
                  data-testid="upi-txn-ref"
                  autoComplete="off"
                />
                <div className="text-[11px] text-muted-foreground">
                  After paying, copy the reference / transaction ID your UPI app shows and paste it here.
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUpiIntent(null); setUpiTxnRef(""); }} disabled={upiSubmitting}>Cancel</Button>
            <Button
              onClick={submitUpiConfirm}
              disabled={upiSubmitting || upiTxnRef.trim().length < 3}
              data-testid="upi-confirm"
              className="gap-1.5"
            >
              {upiSubmitting ? "Recording…" : "I've paid — record it"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Draft invoice editor — replaces the read-only invoice view while status is
// "draft". Lets the user tweak lines, discount, tax, notes, and either
// confirm-and-generate (flip to unpaid) or discard the draft.
// ---------------------------------------------------------------------------
function DraftInvoiceEditor({ inv, canEdit, onSaved, onConfirmed, onDeleted }) {
  const [therapyTypes, setTherapyTypes] = useState([]);
  // Stamp each line with a stable `_uid` so React can reconcile the
  // dynamic list correctly even when the user reorders or deletes rows
  // (code-review iter31: array-index-as-key is a state-bug risk).
  const nextLineUid = useRef(0);
  const stampUid = (l) => ({ ...l, _uid: l._uid || `l-${++nextLineUid.current}` });
  const [lines, setLines] = useState(() => (inv.lines || []).map(stampUid));
  const [discount, setDiscount] = useState(inv.discount || 0);
  const [taxPct, setTaxPct] = useState(inv.tax_pct || 0);
  const [notes, setNotes] = useState(inv.notes || "");
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [grants, setGrants] = useState([]);
  const [grantAmount, setGrantAmount] = useState("");
  const [chosenGrant, setChosenGrant] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/therapy-types");
        setTherapyTypes(data || []);
      } catch (err) { devLog("therapy-types fetch failed", err); }
      // Grants that cover the invoice's student
      if (inv.student_id) {
        try {
          const { data } = await api.get(`/billing/grants-for-student/${inv.student_id}`);
          setGrants(data || []);
        } catch (err) { devLog("grants-for-student fetch failed", err); }
      }
    })();
  }, [inv.student_id]);

  // Live totals — the backend will recompute on save, this is just for preview.
  // Donation lines are subtracted from subtotal before tax.
  let billedSubtotal = 0;
  let donationCredit = 0;
  for (const l of lines) {
    const amt = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
    if ((l.line_type || "billed") === "donation") donationCredit += amt; else billedSubtotal += amt;
  }
  const subtotal = billedSubtotal - donationCredit;
  const afterDisc = Math.max(0, subtotal - Number(discount || 0));
  const taxAmt = Math.round(afterDisc * (Number(taxPct || 0) / 100) * 100) / 100;
  const total = Math.round((afterDisc + taxAmt) * 100) / 100;

  const setLine = (i, key, val) => {
    setLines(prev => {
      const next = [...prev];
      next[i] = { ...next[i], [key]: val };
      return next;
    });
    setDirty(true);
  };
  const addLine = () => { setLines(prev => [...prev, stampUid({ description: "", quantity: 1, unit_price: 0, line_type: "billed" })]); setDirty(true); };
  const removeLine = (i) => { setLines(prev => prev.filter((_, idx) => idx !== i)); setDirty(true); };
  const fillFromTherapy = (i, tt_id) => {
    const tt = therapyTypes.find(t => t.id === tt_id);
    if (!tt) return;
    // iter-67j (Area 3) — respect user edits; only autofill blank rows.
    const line = lines[i];
    const hasDesc = (line?.description || "").trim().length > 0;
    const hasPrice = Number(line?.unit_price || 0) > 0;
    if (hasDesc || hasPrice) return;
    setLines(prev => {
      const next = [...prev];
      next[i] = {
        ...next[i],
        description: `${tt.name} session (${tt.duration_min} min)`,
        unit_price: tt.rate,
        therapy_type_id: tt_id,
      };
      return next;
    });
    setDirty(true);
  };

  const addDonationLine = () => {
    if (!chosenGrant) return;
    const g = grants.find(x => x.id === chosenGrant);
    if (!g) return;
    const raw = Number(grantAmount || 0);
    if (raw <= 0) { toast.error("Enter a donation amount"); return; }
    if (raw > g.balance) {
      toast.error(`Grant "${g.name}" only has ${fmtMoney(g.balance)} remaining`);
      return;
    }
    setLines(prev => [...prev, {
      description: `Donation — ${g.name}${g.donor ? ` (${g.donor})` : ""}`,
      quantity: 1,
      unit_price: raw,
      line_type: "donation",
      grant_id: g.id,
    }]);
    setChosenGrant("");
    setGrantAmount("");
    setDirty(true);
    toast.success("Donation line added");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/billing/invoices/${inv.id}`, {
        lines: lines.map(l => ({
          description: l.description || "",
          quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0,
          therapy_type_id: l.therapy_type_id || null,
          session_id: l.session_id || null,
          line_date: l.line_date || null,
          line_type: l.line_type || "billed",
          grant_id: l.grant_id || null,
        })),
        discount: Number(discount) || 0,
        tax_pct: Number(taxPct) || 0,
        notes,
      });
      toast.success("Draft saved");
      setDirty(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const confirm = async () => {
    // Persist any pending edits first so the confirmed invoice matches what
    // the user sees on screen.
    if (dirty) await save();
    setConfirming(true);
    try {
      await api.post(`/billing/invoices/${inv.id}/confirm`);
      toast.success(`Invoice ${inv.invoice_no} generated`);
      onConfirmed?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setConfirming(false); }
  };

  const discard = async () => {
    if (!window.confirm(`Discard draft ${inv.invoice_no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/billing/invoices/${inv.id}`);
      toast.success("Draft discarded");
      onDeleted?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline mb-1.5">Draft invoice</div>
            <div className="font-display text-3xl font-bold tracking-tight">{inv.invoice_no}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {inv.student?.student_id && (
                <span className="font-mono font-semibold text-primary mr-1">{inv.student.student_id} ·</span>
              )}
              {inv.student?.name} · {fmtDate(inv.created_at)}
              {inv.period_from && inv.period_to && (
                <> · Period: <span className="font-medium text-foreground">{inv.period_from}</span> → <span className="font-medium text-foreground">{inv.period_to}</span></>
              )}
            </div>
          </div>
          <Badge className="bg-slate-200 text-slate-800 border-slate-300">DRAFT</Badge>
        </div>

        <div className="p-3 rounded-md border border-amber-200 bg-amber-50 text-xs text-amber-900">
          This invoice is a <span className="font-semibold">draft</span> — nothing has been sent to guardians yet. Edit the line items, apply discount/tax, then click <span className="font-semibold">Confirm &amp; Generate</span> to finalize it.
        </div>

        {/* Line items — editable */}
        <div className="space-y-2">
          <Label>Line items</Label>
          {lines.length === 0 && (
            <div className="text-xs text-muted-foreground italic">No line items yet — add one below.</div>
          )}
          <div className="space-y-2">
            {lines.map((l, i) => {
              const isDonation = (l.line_type || "billed") === "donation";
              return (
              <div
                key={l._uid || `line-${i}`}
                className={`grid grid-cols-12 gap-2 items-start p-2 rounded border ${isDonation ? "border-emerald-200 bg-emerald-50/40" : "border-border"}`}
                data-testid={`draft-line-${i}`}
              >
                <div className="col-span-12 sm:col-span-5">
                  {isDonation && (
                    <Badge className="mb-1 bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Donation credit</Badge>
                  )}
                  <Input
                    placeholder="Description"
                    value={l.description || ""}
                    onChange={e => setLine(i, "description", e.target.value)}
                    disabled={!canEdit}
                  />
                  {canEdit && !isDonation && (
                    <Select onValueChange={v => fillFromTherapy(i, v)}>
                      <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Fill from therapy…" /></SelectTrigger>
                      <SelectContent className="bg-card">
                        {therapyTypes.map(tt => <SelectItem key={tt.id} value={tt.id}>{tt.name} · {fmtMoney(tt.rate)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {l.line_date && (
                    <div className="text-[10px] text-muted-foreground mt-1">Session date: {l.line_date}</div>
                  )}
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Qty</Label>
                  <Input type="number" min="1" value={l.quantity} onChange={e => setLine(i, "quantity", e.target.value)} disabled={!canEdit} />
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{isDonation ? "Amount" : "Unit price"}</Label>
                  <Input type="number" min="0" step="0.01" value={l.unit_price} onChange={e => setLine(i, "unit_price", e.target.value)} disabled={!canEdit} />
                </div>
                <div className="col-span-2 sm:col-span-1 flex items-end justify-end pt-4">
                  <div className={`text-sm font-medium tabular-nums text-right ${isDonation ? "text-emerald-700" : ""}`}>
                    {isDonation ? "−" : ""}{fmtMoney((Number(l.quantity) || 0) * (Number(l.unit_price) || 0))}
                  </div>
                </div>
                {canEdit && (
                  <div className="col-span-12 sm:col-span-1 flex justify-end pt-4">
                    <Button variant="ghost" size="icon" onClick={() => removeLine(i)} className="text-red-600 hover:text-red-700" data-testid={`draft-remove-line-${i}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
          {canEdit && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={addLine} className="gap-1.5" data-testid="draft-add-line">
                <Plus size={14} /> Add line
              </Button>
            </div>
          )}
        </div>

        {/* Grant → Donation picker: only visible if the student has grants covering them. */}
        {canEdit && grants.length > 0 && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid="grant-picker-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-sm font-medium text-emerald-900">Apply donation from a grant</div>
                <div className="text-[11px] text-emerald-800/80">This student is covered by {grants.length} grant{grants.length !== 1 ? "s" : ""}. Add a donation credit that reduces the payable amount and auto-allocates against the grant on confirm.</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Select value={chosenGrant} onValueChange={setChosenGrant}>
                <SelectTrigger className="sm:col-span-2" data-testid="grant-select"><SelectValue placeholder="Choose a grant" /></SelectTrigger>
                <SelectContent className="bg-card">
                  {grants.map(g => (
                    <SelectItem key={g.id} value={g.id} disabled={g.balance <= 0}>
                      {g.name}{g.donor ? ` · ${g.donor}` : ""} · balance {fmtMoney(g.balance)}{g.balance <= 0 ? " (exhausted)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Input
                  type="number" min="0" step="0.01"
                  placeholder="Amount"
                  value={grantAmount}
                  onChange={e => setGrantAmount(e.target.value)}
                  data-testid="grant-amount"
                />
                <Button onClick={addDonationLine} disabled={!chosenGrant || !grantAmount} data-testid="add-donation-line">Add</Button>
              </div>
            </div>
          </div>
        )}

        {/* Discount / tax / notes */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Discount (₹)</Label>
            <Input type="number" min="0" step="0.01" value={discount} onChange={e => { setDiscount(e.target.value); setDirty(true); }} disabled={!canEdit} data-testid="draft-discount" />
          </div>
          <div className="space-y-1.5">
            <Label>Tax %</Label>
            <Input type="number" min="0" step="0.01" value={taxPct} onChange={e => { setTaxPct(e.target.value); setDirty(true); }} disabled={!canEdit} data-testid="draft-tax" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Notes</Label>
          <Textarea rows={2} value={notes} onChange={e => { setNotes(e.target.value); setDirty(true); }} disabled={!canEdit} data-testid="draft-notes" />
        </div>

        {/* Totals preview */}
        <div className="p-4 rounded-md border border-border bg-muted/40 text-sm tabular-nums max-w-xs ml-auto space-y-1">
          <div className="flex justify-between"><span className="text-muted-foreground">Billed subtotal</span><span>{fmtMoney(billedSubtotal)}</span></div>
          {donationCredit > 0 && (
            <div className="flex justify-between text-emerald-700"><span>Donation credit</span><span>−{fmtMoney(donationCredit)}</span></div>
          )}
          <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span>−{fmtMoney(discount)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span>{fmtMoney(taxAmt)}</span></div>
          <div className="flex justify-between font-display font-bold text-lg pt-2 border-t border-border"><span>Total</span><span>{fmtMoney(total)}</span></div>
        </div>

        {canEdit && (
          <div className="flex flex-col sm:flex-row gap-2 justify-end pt-2 border-t border-border">
            <Button variant="ghost" onClick={discard} className="text-red-600 hover:text-red-700" data-testid="draft-discard">
              Discard draft
            </Button>
            <Button variant="outline" onClick={save} disabled={saving || confirming} data-testid="draft-save">
              {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </Button>
            <Button onClick={confirm} disabled={saving || confirming || total <= 0 || lines.length === 0} className="gap-1.5" data-testid="draft-confirm">
              <CheckCircle size={16} weight="duotone" />
              {confirming ? "Generating…" : "Confirm & Generate"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
